import { ChevronLeft, ChevronRight } from "lucide-react";

type Props = {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  siblingCount?: number;   // how many numbers to show around current
  boundaryCount?: number;  // how many numbers to always show at ends
};

const range = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

function usePagination(
  totalPages: number,
  currentPage: number,
  siblingCount = 1,
  boundaryCount = 1
) {
  const first = 1;
  const last = totalPages;

  const left = Math.max(currentPage - siblingCount, first);
  const right = Math.min(currentPage + siblingCount, last);

  const showLeftDots = left > first + boundaryCount;
  const showRightDots = right < last - boundaryCount;

  const leftBoundary = range(first, Math.min(boundaryCount, last));
  const middle = range(Math.max(left, first + boundaryCount), Math.min(right, last - boundaryCount));
  const rightBoundary = range(Math.max(last - boundaryCount + 1, first), last);

  const pages: (number | "...")[] = [];

  pages.push(...leftBoundary);

  if (showLeftDots) pages.push("...");

  // avoid duplicates when ranges touch
  const middleStart = pages.length ? (typeof pages[pages.length - 1] === "number" ? (pages[pages.length - 1] as number) + 1 : -Infinity) : -Infinity;
  const middleClean = middle.filter(p => p >= middleStart);
  pages.push(...middleClean);

  if (showRightDots) pages.push("...");

  const rightStart = pages.length ? (typeof pages[pages.length - 1] === "number" ? (pages[pages.length - 1] as number) + 1 : -Infinity) : -Infinity;
  const rightClean = rightBoundary.filter(p => p >= rightStart);
  pages.push(...rightClean);

  return pages;
}

export const Pagination = ({
  totalPages,
  currentPage,
  onPageChange,
  siblingCount = 1,
  boundaryCount = 1,
}: Props) => {
  const pages = usePagination(totalPages, currentPage, siblingCount, boundaryCount);

  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  return (
    <nav className="flex items-center gap-5 text-sm text-gray-600 select-none justify-center" aria-label="Pagination">
      <button
        type="button"
        className="disabled:opacity-30"
        onClick={() => canPrev && onPageChange(currentPage - 1)}
        disabled={!canPrev}
        aria-label="Previous page"
      >
        <ChevronLeft />
      </button>

      <div className="flex items-center gap-2">
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`dots-${i}`} className="pointer-events-none">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
              className={
                p === currentPage
                  ? "w-10 h-10 rounded-full bg-ring text-white font-medium flex items-center justify-center"
                  : "w-10 h-10 text-gray-700 hover:text-gray-900"
              }
            >
              {p}
            </button>
          )
        )}
      </div>

      <button
        type="button"
        className="disabled:opacity-30"
        onClick={() => canNext && onPageChange(currentPage + 1)}
        disabled={!canNext}
        aria-label="Next page"
      >
        <ChevronRight />
      </button>
    </nav>
  );
}